// Vendors
import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';

// Constants
import {
  InsuranceDetailsState,
  InsuranceTabProps,
  InsuranceRequest,
  EventData,
} from '@/utils/types';
import {
  insurancePolicyEvents,
  insuranceDefeciencyEvents,
  insuranceCommunicationEvents,
  insuranceLetterEvents,
} from '@/utils/constants/insuranceTab';

// Components
import Tabs from '@/components/atoms/Tabs/Tabs';
import { EventsList } from './EventsList';
import Loader from '@/components/atoms/Loader/Loader';

// Store
import { fetchInsuranceDetailsRequest } from '@/store/features/insuranceDetails/insuranceDetailsSlice';
import { useAppDispatch, useAppSelector } from '@/hooks/storeHook';

const InsuranceTabs = ['All', 'Policies', 'Emails/Calls', 'Letters', 'Deficiencies'];
const Zero = 0;

const buildDeficiencyDedupKey = (event: EventData): string => {
  const deficiencyCodes = (event.deficiencyCode ?? [])
     .map((code) => code.trim())
     .sort()
     .join('|');
  return [
    event.enteredOn,
    event.eventType,
    event.placement,
    event.deficient,
    event.policyNumber,
    event.insCompany,
    deficiencyCodes,
    event.deficiencyHistory,
  ]
    .map((value) => String(value ?? '').trim())
    .join('::');
};

const dedupeEvents = (events: EventData[], keyBuilder: (event: EventData) => string): EventData[] => {
  const seen = new Set<string>();

  return events.filter((event) => {
    const key = keyBuilder(event);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const filterInsuranceEventList = (events: EventData[], selectedTab: number): EventData[] => {
  const filterMap: Record<number, string[]> = {
    1: insurancePolicyEvents,
    2: insuranceCommunicationEvents,
    3: insuranceLetterEvents,
    4: insuranceDefeciencyEvents,
  };

  if (selectedTab === Zero) {
    const seenDeficiencyKeys = new Set<string>();
    return [...events]
      .flatMap((event) => {
        if (event.placement === 'VOL' && event.deficient === 'Y') {
          const key = buildDeficiencyDedupKey(event);
          if (seenDeficiencyKeys.has(key)) {
            return [event];
          }
          seenDeficiencyKeys.add(key);
          return [event, { ...event, forceDeficiencyEvent: true }];
        }
        return [event];
      })
      .sort((a, b) => new Date(b.enteredOn).getTime() - new Date(a.enteredOn).getTime());
  }

  const filteredKeys = filterMap[selectedTab] ?? [];
  const validTypes = ['CANCEL', 'NEWPOL', 'ENDORS', 'RENEW', 'NRENEW', 'REINST'];

  const filteredEvents = events.filter(({ placement, eventType, deficient }) => {
      switch (selectedTab) {
        case 1: // Insurance Policy Events
          return (
            validTypes.includes(eventType) &&
            (placement === 'FP' || placement === 'VOL')
          );

        case 2: // Communication Events
          return filteredKeys.includes(eventType);
        case 3: // Letter Events
          return filteredKeys.includes(eventType);

        case 4: // Deficiency Events
          return placement === 'VOL' && deficient === 'Y';

        default:
          return false;
      }
    });

  const dedupedEvents =
    selectedTab === 4
      ? dedupeEvents(filteredEvents, buildDeficiencyDedupKey)
      : filteredEvents;

  return dedupedEvents.sort(
    (a, b) => new Date(b.enteredOn).getTime() - new Date(a.enteredOn).getTime(),
  );
};
export const InsuranceTab: React.FC<InsuranceTabProps> = ({
  contractId,
  clientId,
  onDeficiencyClick,
  onPolicyBillingClick,
  onOIPolicyClick,
}) => {
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState<number>(Zero);

  const { insuranceEventsList = [], loading } = useAppSelector(
    (state: { insuranceDetails: InsuranceDetailsState }) => state.insuranceDetails,
  );

  useEffect(() => {
    var request: InsuranceRequest = {
      contractId: contractId,
      clientId:clientId,
      keyword: '',
      pageNo: 0,
      pageSize: 0,
    };
    dispatch(fetchInsuranceDetailsRequest(request));
  }, [dispatch, contractId]);

  const SelectedEventList = useMemo(() => {
    return insuranceEventsList.length
      ? filterInsuranceEventList(insuranceEventsList, selectedTab)
      : [];
  }, [insuranceEventsList, selectedTab]);

  return (
    <Box className="insurance-tab-container">
      <Tabs
        labels={InsuranceTabs}
        selectedTab={selectedTab}
        onChange={(_, newValue) =>{ 
          setSelectedTab(Number(newValue))
          window.dataLayer.push({
            event:'tabs_selected',
            tab_name: selectedTab
          })
        }}
        showTickIcon={true}
        containerName="sub-tab-container"
      />

      <div className="events-list_scrollable-main">
        {loading ? (
          <div className="loader-center">
            <Loader />
          </div>
        ) : (
          <EventsList
              eventsList={SelectedEventList}
              selectedTab={selectedTab}
              onDeficiencyClick={onDeficiencyClick}
              onPolicyBillingClick={onPolicyBillingClick} clientId={clientId}  onOIPolicyClick={onOIPolicyClick}        />
        )}
      </div>
    </Box>
  );
};
